import { gql } from '@apollo/client';

export const DetailedBusinessFragment = gql`
    fragment detailedBusiness on Business {
        name
        fullName
        status
        country
        locale
        timezone
        defaultCurrency
        taxId
        contactEmail
        businessWebsite
        businessPhoneNumber
        smsPhoneNumber
        facebookLink
        twitterLink
        instagramLink
        uiLogoUrl
        uiIconUrl
        uiFaviconUrl
        uiBackgroundImageUrl
        uiFormBackgroundImageUrl
        uiBackgroundColor
        uiAlternateBackgroundColor
        uiPrimaryColor
        uiSecondaryColor
        uiPrimaryFontColor
        uiSecondaryFontColor
        uiSessionSelfBookingDefaultView
        hideSignupFeeInSelfBooking
        hidePricingOnClientProfile
        hideStartingAtPricingInSelfBooking
        genderDisplayOption
        simplifiedGenderInput
        formsPhoneNumberRequired
        clientCustomAttributes
        clientStatuses
        defaultClientStatus
        prospectLostStatus
        tagCategories {
            name
            tagType
            tags
        }
        welcomeMessage
        welcomeEmailClient
        welcomeEmailStaff
        promotionalMessages {
            message
            includeButton
            buttonText
            buttonUrl
        }
        googleReviewEmailSubject
        googleReviewPrompt
        enableGoogleReviewPrompt
        emailFrom
        bccEmail
        tagline
        footer
        paymentGatewayId
        paymentGatewayApiId
        paymentGatewayApiKey
        achPaymentGatewayApiId
        achPaymentGatewayApiKey
        surchargeDetails {
            basisPoints
        }
        collectBillingPostalCode
        achPaymentEnabled
        achPaymentDisableClientAddAccount
        autoPayDisabled
        migrateToOpus1Payments
        notificationsEnabled
        notificationsEnabledOwnersOnly
        internalVideoConfEnabled
        enableClientPosting
        enableStaffSharedFolder
        enableCombinedPayments
        clientPostingDisclaimer
        paymentTerminals {
            id
            label
        }
        locations {
            id
            business
            name
        }
        mailchimpSyncEnabled
        mailchimpSyncId
        mailchimpSyncListId
        mailchimpLastSync
        mailchimpSyncKey
        mailchimpLastSyncError
        merchantId
        achMerchantId
        stripeCustomersOnPlatform
        stripeTokenizerEnabled
        merchantFeeRate
        widgetLinks {
            link
            title
            description
            photo
            widgetType
            serviceIds
            paymentPlanTypes
            staffIds
            locationIds
            visibleByVisitors
            callToAction
            callToActionTitle
            checkWeekly
            defaultPrimarySelection
            services {
                id
                durationMinutes
                hasSessions
                name
                deactivated
                plans {
                    name
                    onlyPayableWithCredit
                    paymentInterval
                    enabledForSelfBooking
                    paymentType
                    visits
                    visitPreEnroll
                    visitPreEnrollHoldDays
                    locations {
                        id
                    }
                }
                requiresBooking
                tags
            }
            staff {
                id
                name
                profilePhoto
                staffSecondaryAttribute
            }
            locations {
                id
                name
            }
        }
        landingPages {
            link
            widgetLinks
        }
        reportTemplates {
            id
            questions {
                title
                inputType
                options
            }
        }
        emailDomains {
            domain
            status
            dnsRecords {
                type
                name
                value
            }
        }
        planAmountDue
        plan {
            accountStatus
            freeUntil
            endAt
            planType
            rate
            currency
            discount
            discountReason
            nextPaymentDue
            lastSuccessfulPayment
            paymentMethod
            paymentGatewayCustomerId
            premiumFeatures {
                label
                amount
                startAt
            }
        }
        prospectActiveStudents
        prospectActiveLocations
        prospectCurrentSoftware
        prospectPainPoints
        perLocationSms
        smsFeatureEnabled
        mailchimpFeatureEnabled
        smsTier
        maxPerFileSizeUsageMegaBytes
        maxFileSizeUsageMegaBytes
        currentFileSizeUsageMegaBytes
        smsCountTotal
        lessonMateFeatureEnabled
        videoRecordingRequestEnabled
        marketingPlusEnabled
        campaignFeatureEnabled
        ongoingCampaignsEnabled
        advancedPayrollSettingsEnabled
        voipEnabled
        marketingPlusReviewWidgetEnabled
        marketingPlusReviewWidgetMobileDisplay
        marketingPlusReviewWidgetLocations {
            id
            name
        }
        clientDefaultNotificationOptOuts
        clientDefaultSmsNotificationOptOuts
        staffDefaultNotificationOptOuts
        staffDefaultSmsNotificationOptOuts
        smsAppointmentUpdatesIncludeStaffBio
        webhooksEnabled
        federatedLoginEnabled
        crmEnabled
        advancedReportingEnabled
        webhookKey
        webhooks {
            name
            status
            trigger
            direction
            url
            userAgent
            lastFailureDetails
            lastFailureDate
            lastSuccessDate
        }
        selfRegisterFields {
            id
            name
            fieldType
            showField
            requireField
            fieldChoices
            clientCustomAttribute
            autoTagClient
            conditionalOptions {
                selectedChoice
                choices
            }
            conditionalFieldId
        }
        crmLeadCaptureFields {
            id
            name
            fieldType
            showField
            requireField
            fieldChoices
            clientCustomAttribute
            autoTagClient
            conditionalOptions {
                selectedChoice
                choices
            }
            conditionalFieldId
        }
        presetDiscounts {
            label
            discountType
            discountTarget
            rate
            repeating
        }
        crmWelcomeMessage
        crmThankYouMessage
        crmAutoAssignStaff {
            staff {
                id
                name
            }
            priority
        }
        timeOffPolicy
        timeOffPolicyEnabled
        timeOffCategories
        birthdayMessage
        sendBirthdayMessageToActiveMembers
        photoReleaseEnabled
        photoReleaseMigratedAt
        photoReleaseMigrationRequired
        includeStudentsNameInIcal
        paymentFailedTemplate
        paymentReminderTemplate
        dataCollectionPrivacyPolicyUrl
        messagingTermsAndConditionsUrl
        aiCustomizableCallAnalysisPrompt
        variableRatePlanDefaultHouseholdDiscountTarget
        tuitionPolicyEnabled
        mobilePaymentsBeta
        version
    }
`;

/** Same fields as detailedBusiness but widgetLinks omit nested services/plans/staff/locations (smaller updateBusiness response). */
export const BusinessAfterUpdateFragment = gql`
    fragment businessAfterUpdate on Business {
        name
        fullName
        status
        country
        locale
        timezone
        defaultCurrency
        taxId
        contactEmail
        businessWebsite
        businessPhoneNumber
        smsPhoneNumber
        facebookLink
        twitterLink
        instagramLink
        uiLogoUrl
        uiIconUrl
        uiFaviconUrl
        uiBackgroundImageUrl
        uiFormBackgroundImageUrl
        uiBackgroundColor
        uiAlternateBackgroundColor
        uiPrimaryColor
        uiSecondaryColor
        uiPrimaryFontColor
        uiSecondaryFontColor
        uiSessionSelfBookingDefaultView
        hideSignupFeeInSelfBooking
        hidePricingOnClientProfile
        hideStartingAtPricingInSelfBooking
        genderDisplayOption
        simplifiedGenderInput
        formsPhoneNumberRequired
        clientCustomAttributes
        clientStatuses
        defaultClientStatus
        prospectLostStatus
        tagCategories {
            name
            tagType
            tags
        }
        welcomeMessage
        welcomeEmailClient
        welcomeEmailStaff
        promotionalMessages {
            message
            includeButton
            buttonText
            buttonUrl
        }
        googleReviewEmailSubject
        googleReviewPrompt
        enableGoogleReviewPrompt
        emailFrom
        bccEmail
        tagline
        footer
        paymentGatewayId
        paymentGatewayApiId
        paymentGatewayApiKey
        achPaymentGatewayApiId
        achPaymentGatewayApiKey
        surchargeDetails {
            basisPoints
        }
        collectBillingPostalCode
        achPaymentEnabled
        achPaymentDisableClientAddAccount
        autoPayDisabled
        migrateToOpus1Payments
        notificationsEnabled
        notificationsEnabledOwnersOnly
        internalVideoConfEnabled
        enableClientPosting
        enableStaffSharedFolder
        enableCombinedPayments
        clientPostingDisclaimer
        paymentTerminals {
            id
            label
        }
        locations {
            id
            business
            name
        }
        mailchimpSyncEnabled
        mailchimpSyncId
        mailchimpSyncListId
        mailchimpLastSync
        mailchimpSyncKey
        mailchimpLastSyncError
        merchantId
        achMerchantId
        stripeCustomersOnPlatform
        stripeTokenizerEnabled
        merchantFeeRate
        widgetLinks {
            link
            title
            description
            photo
            widgetType
            serviceIds
            paymentPlanTypes
            staffIds
            locationIds
            visibleByVisitors
            callToAction
            callToActionTitle
            checkWeekly
            defaultPrimarySelection
        }
        landingPages {
            link
            widgetLinks
        }
        reportTemplates {
            id
            questions {
                title
                inputType
                options
            }
        }
        emailDomains {
            domain
            status
            dnsRecords {
                type
                name
                value
            }
        }
        planAmountDue
        plan {
            accountStatus
            freeUntil
            endAt
            planType
            rate
            currency
            discount
            discountReason
            nextPaymentDue
            lastSuccessfulPayment
            paymentMethod
            paymentGatewayCustomerId
            premiumFeatures {
                label
                amount
                startAt
            }
        }
        prospectActiveStudents
        prospectActiveLocations
        prospectCurrentSoftware
        prospectPainPoints
        perLocationSms
        smsFeatureEnabled
        mailchimpFeatureEnabled
        smsTier
        maxPerFileSizeUsageMegaBytes
        maxFileSizeUsageMegaBytes
        currentFileSizeUsageMegaBytes
        smsCountTotal
        lessonMateFeatureEnabled
        videoRecordingRequestEnabled
        marketingPlusEnabled
        campaignFeatureEnabled
        ongoingCampaignsEnabled
        advancedPayrollSettingsEnabled
        voipEnabled
        marketingPlusReviewWidgetEnabled
        marketingPlusReviewWidgetMobileDisplay
        marketingPlusReviewWidgetLocations {
            id
            name
        }
        clientDefaultNotificationOptOuts
        clientDefaultSmsNotificationOptOuts
        staffDefaultNotificationOptOuts
        staffDefaultSmsNotificationOptOuts
        smsAppointmentUpdatesIncludeStaffBio
        webhooksEnabled
        federatedLoginEnabled
        crmEnabled
        advancedReportingEnabled
        webhookKey
        webhooks {
            name
            status
            trigger
            direction
            url
            userAgent
            lastFailureDetails
            lastFailureDate
            lastSuccessDate
        }
        selfRegisterFields {
            id
            name
            fieldType
            showField
            requireField
            fieldChoices
            clientCustomAttribute
            autoTagClient
            conditionalOptions {
                selectedChoice
                choices
            }
            conditionalFieldId
        }
        crmLeadCaptureFields {
            id
            name
            fieldType
            showField
            requireField
            fieldChoices
            clientCustomAttribute
            autoTagClient
            conditionalOptions {
                selectedChoice
                choices
            }
            conditionalFieldId
        }
        presetDiscounts {
            label
            discountType
            discountTarget
            rate
            repeating
        }
        crmWelcomeMessage
        crmThankYouMessage
        crmAutoAssignStaff {
            staff {
                id
                name
            }
            priority
        }
        timeOffPolicy
        timeOffPolicyEnabled
        timeOffCategories
        birthdayMessage
        sendBirthdayMessageToActiveMembers
        photoReleaseEnabled
        photoReleaseMigratedAt
        photoReleaseMigrationRequired
        includeStudentsNameInIcal
        paymentFailedTemplate
        paymentReminderTemplate
        dataCollectionPrivacyPolicyUrl
        messagingTermsAndConditionsUrl
        aiCustomizableCallAnalysisPrompt
        variableRatePlanDefaultHouseholdDiscountTarget
        tuitionPolicyEnabled
        mobilePaymentsBeta
        version
    }
`;
export const BUSINESS_EXIST_QUERY = gql`
    query businessExists($business: ID!) {
        business(business: $business) {
            name
        }
    }
`;

export const CHECK_BUSINESS_NAME_AVAILABILITY = gql`
    query checkBusinessNameAvailability($business: ID!) {
        checkBusinessNameAvailability(business: $business) {
            available
        }
    }
`;

export const BUSINESS_QUERY = gql`
    query businessQuery($businessName: ID!) {
        business(business: $businessName) {
            name
            fullName
            status
            contactEmail
            emailFrom
            country
            timezone
            uiLogoUrl
            uiIconUrl
            uiFaviconUrl
            uiBackgroundImageUrl
            uiFormBackgroundImageUrl
            uiPrimaryColor
            uiSecondaryColor
            uiBackgroundColor
            uiAlternateBackgroundColor
            uiPrimaryFontColor
            uiSecondaryFontColor
            uiSessionSelfBookingDefaultView
            hideSignupFeeInSelfBooking
            hidePricingOnClientProfile
            hideStartingAtPricingInSelfBooking
            genderDisplayOption
            simplifiedGenderInput
            formsPhoneNumberRequired
            paymentGatewayId
            achPaymentEnabled
            achPaymentDisableClientAddAccount
            autoPayDisabled
            merchantId
            stripeCustomersOnPlatform
            stripeTokenizerEnabled
            collectBillingPostalCode
            surchargeDetails {
                basisPoints
            }
            roleNames
            tagline
            clientStatuses
            defaultClientStatus
            prospectLostStatus
            clientCustomAttributes
            welcomeMessage
            businessWebsite
            businessPhoneNumber
            smsPhoneNumber
            smsTier
            maxPerFileSizeUsageMegaBytes
            maxFileSizeUsageMegaBytes
            currentFileSizeUsageMegaBytes
            smsCountTotal
            googleTagManagerId
            marketingPlusEnabled
            campaignFeatureEnabled
            ongoingCampaignsEnabled
            advancedPayrollSettingsEnabled
            voipEnabled
            widgetLinks {
                link
                title
                description
                photo
                widgetType
                serviceIds
                paymentPlanTypes
                staffIds
                locationIds
                visibleByVisitors
                callToAction
                callToActionTitle
                checkWeekly
                defaultPrimarySelection
            }
            landingPages {
                link
                widgetLinks
            }
            reportTemplates {
                id
                questions {
                    title
                    inputType
                    options
                }
            }
            enableClientPosting
            enableStaffSharedFolder
            enableCombinedPayments
            clientPostingDisclaimer
            internalVideoConfEnabled
            facebookLink
            instagramLink
            twitterLink
            defaultCurrency
            locale
            perLocationSms
            smsFeatureEnabled
            mailchimpFeatureEnabled
            lessonMateFeatureEnabled
            videoRecordingRequestEnabled
            marketingPlusEnabled
            campaignFeatureEnabled
            ongoingCampaignsEnabled
            advancedPayrollSettingsEnabled
            voipEnabled
            clientDefaultNotificationOptOuts
            clientDefaultSmsNotificationOptOuts
            staffDefaultNotificationOptOuts
            staffDefaultSmsNotificationOptOuts
            smsAppointmentUpdatesIncludeStaffBio
            webhooksEnabled
            federatedLoginEnabled
            webhookKey
            crmEnabled
            advancedReportingEnabled
            webhooks {
                name
                status
                direction
                trigger
                url
                userAgent
                lastFailureDate
                lastFailureDetails
                lastSuccessDate
            }
            selfRegisterFields {
                id
                name
                fieldType
                showField
                requireField
                fieldChoices
                clientCustomAttribute
                autoTagClient
                conditionalOptions {
                    selectedChoice
                    choices
                }
                conditionalFieldId
            }
            crmLeadCaptureFields {
                id
                name
                fieldType
                showField
                requireField
                fieldChoices
                clientCustomAttribute
                autoTagClient
                conditionalOptions {
                    selectedChoice
                    choices
                }
                conditionalFieldId
            }

            crmWelcomeMessage
            crmThankYouMessage
            crmAutoAssignStaff {
                staff {
                    id
                    name
                }
                priority
            }
            timeOffPolicy
            timeOffPolicyEnabled
            timeOffCategories
            birthdayMessage
            sendBirthdayMessageToActiveMembers
            photoReleaseEnabled
            photoReleaseMigratedAt
            photoReleaseMigrationRequired
            includeStudentsNameInIcal
            paymentFailedTemplate
            paymentReminderTemplate
            dataCollectionPrivacyPolicyUrl
            messagingTermsAndConditionsUrl
            variableRatePlanDefaultHouseholdDiscountTarget
            voipEnabled
            tuitionPolicyEnabled
            mobilePaymentsBeta
        }
    }
`;

export const BUSINESS_QUERY_FOR_APP = gql`
    query businessQueryForApp($businessName: ID!) {
        business(business: $businessName) {
            name
            fullName
            domainReady
            status
            contactEmail
            emailFrom
            country
            timezone
            uiLogoUrl
            uiIconUrl
            uiFaviconUrl
            uiBackgroundImageUrl
            uiFormBackgroundImageUrl
            uiPrimaryColor
            uiSecondaryColor
            uiBackgroundColor
            uiAlternateBackgroundColor
            uiPrimaryFontColor
            uiSecondaryFontColor
            uiSessionSelfBookingDefaultView
            hideSignupFeeInSelfBooking
            hidePricingOnClientProfile
            hideStartingAtPricingInSelfBooking
            genderDisplayOption
            simplifiedGenderInput
            formsPhoneNumberRequired
            paymentGatewayId
            achPaymentEnabled
            achPaymentDisableClientAddAccount
            autoPayDisabled
            merchantId
            stripeCustomersOnPlatform
            stripeTokenizerEnabled
            collectBillingPostalCode
            surchargeDetails {
                basisPoints
            }
            roleNames
            tagline
            clientStatuses
            defaultClientStatus
            prospectLostStatus
            clientCustomAttributes
            welcomeMessage
            businessWebsite
            businessPhoneNumber
            smsPhoneNumber
            smsTier
            maxPerFileSizeUsageMegaBytes
            maxFileSizeUsageMegaBytes
            currentFileSizeUsageMegaBytes
            smsCountTotal
            googleTagManagerId
            marketingPlusEnabled
            campaignFeatureEnabled
            ongoingCampaignsEnabled
            advancedPayrollSettingsEnabled
            voipEnabled
            widgetLinks {
                link
                widgetType
                serviceIds
                paymentPlanTypes
                staffIds
                locationIds
                visibleByVisitors
                callToAction
                callToActionTitle
                checkWeekly
                defaultPrimarySelection
            }
            reportTemplates {
                id
                questions {
                    title
                    inputType
                    options
                }
            }
            enableClientPosting
            enableStaffSharedFolder
            clientPostingDisclaimer
            internalVideoConfEnabled
            facebookLink
            instagramLink
            twitterLink
            defaultCurrency
            locale
            perLocationSms
            smsFeatureEnabled
            mailchimpFeatureEnabled
            lessonMateFeatureEnabled
            videoRecordingRequestEnabled
            marketingPlusEnabled
            campaignFeatureEnabled
            ongoingCampaignsEnabled
            advancedPayrollSettingsEnabled
            voipEnabled
            clientDefaultNotificationOptOuts
            clientDefaultSmsNotificationOptOuts
            staffDefaultNotificationOptOuts
            staffDefaultSmsNotificationOptOuts
            smsAppointmentUpdatesIncludeStaffBio
            webhooksEnabled
            federatedLoginEnabled
            webhookKey
            crmEnabled
            webhooks {
                name
                status
                direction
                trigger
                url
                userAgent
                lastFailureDate
                lastFailureDetails
                lastSuccessDate
            }
            selfRegisterFields {
                id
                name
                fieldType
                showField
                requireField
                fieldChoices
                clientCustomAttribute
                autoTagClient
                conditionalOptions {
                    selectedChoice
                    choices
                }
                conditionalFieldId
            }
            crmLeadCaptureFields {
                id
                name
                fieldType
                showField
                requireField
                fieldChoices
                clientCustomAttribute
                autoTagClient
                conditionalOptions {
                    selectedChoice
                    choices
                }
                conditionalFieldId
            }

            crmWelcomeMessage
            crmThankYouMessage
            crmAutoAssignStaff {
                staff {
                    id
                    name
                }
                priority
            }
            timeOffPolicy
            timeOffPolicyEnabled
            birthdayMessage
            sendBirthdayMessageToActiveMembers
            photoReleaseEnabled
            photoReleaseMigratedAt
            photoReleaseMigrationRequired
            includeStudentsNameInIcal
            paymentFailedTemplate
            paymentReminderTemplate
            variableRatePlanDefaultHouseholdDiscountTarget
            tuitionPolicyEnabled
            mobilePaymentsBeta
        }
    }
`;

export const INTERNAL_BUSINESS_QUERY = gql`
    query internalBusinessQuery($businessName: ID!) {
        business(business: $businessName) {
            name
            fullName
            contactEmail
            emailFrom
            uiLogoUrl
        }
    }
`;

export const SELECT_BUSINESS_QUERY = gql`
    query selectBusinessesQuery {
        allBusinesses {
            name
            fullName
            contactEmail
            emailFrom
            uiLogoUrl
            uiBackgroundImageUrl
            uiFormBackgroundImageUrl
            uiPrimaryColor
            uiSecondaryColor
            paymentGatewayId
            tagline
        }
    }
`;

export const BUSINESS_LIST_DETAILS_FRAGMENT = gql`
    fragment BusinessListDetailsFragment on Business {
        name
        fullName
        contactEmail
        createdAt
        prospectCompletion
        prospectLikelihood
        emailFrom
        businessWebsite
        uiIconUrl
        uiFaviconUrl
        status
        defaultCurrency
        businessWebsite
        plan {
            accountStatus
            nextPaymentDue
            endAt
            paymentMethod
            premiumFeatures {
                label
                amount
                startAt
            }
        }
        paymentGatewayId
        planAmountDue
        activitiesLast1d
        activitiesLast7d
        activitiesLast30d
        invoiceDueNextMonth
        smsCountTotal
        smsTier
        maxPerFileSizeUsageMegaBytes
        maxFileSizeUsageMegaBytes
        currentFileSizeUsageMegaBytes
        marketingPlusEnabled
        campaignFeatureEnabled
        ongoingCampaignsEnabled
        advancedPayrollSettingsEnabled
        voipEnabled
        crmEnabled
        smsFeatureEnabled
        mailchimpFeatureEnabled
        voipEnabled
        hsDealId
        autoPayDisabled
        notificationsEnabled
    }
`;

export const BUSINESS_LIST_QUERY = gql`
    query businessListQuery {
        allBusinesses {
            ...BusinessListDetailsFragment
        }
    }
    ${BUSINESS_LIST_DETAILS_FRAGMENT}
`;

export const FULL_BUSINESS_QUERY = gql`
    query fullBusinessQuery($businessName: ID!) {
        business(business: $businessName) {
            name
            status
            fullName
            country
            locale
            timezone
            taxId
            defaultCurrency
            contactEmail
            emailFrom
            bccEmail
            businessWebsite
            businessPhoneNumber
            smsPhoneNumber
            facebookLink
            twitterLink
            instagramLink
            uiLogoUrl
            uiIconUrl
            uiFaviconUrl
            uiBackgroundImageUrl
            uiFormBackgroundImageUrl
            uiBackgroundColor
            uiAlternateBackgroundColor
            uiPrimaryColor
            uiSecondaryColor
            uiPrimaryFontColor
            uiSecondaryFontColor
            uiSessionSelfBookingDefaultView
            hideSignupFeeInSelfBooking
            hidePricingOnClientProfile
            hideStartingAtPricingInSelfBooking
            genderDisplayOption
            simplifiedGenderInput
            formsPhoneNumberRequired
            clientCustomAttributes
            welcomeMessage
            welcomeEmailClient
            welcomeEmailStaff
            promotionalMessages {
                message
                includeButton
                buttonText
                buttonUrl
            }
            googleReviewEmailSubject
            googleReviewPrompt
            enableCombinedPayments
            enableGoogleReviewPrompt
            emailFrom
            tagline
            footer
            paymentGatewayId
            paymentGatewayApiId
            paymentGatewayApiKey
            achPaymentGatewayApiId
            achPaymentGatewayApiKey
            merchantId
            migrateToOpus1Payments
            achMerchantId
            stripeCustomersOnPlatform
            multiEntitySetupStatus {
                state
                message
                vaultedCardCount
                distinctActiveMerchantCount
            }
            stripeTokenizerEnabled
            surchargeDetails {
                basisPoints
            }
            collectBillingPostalCode
            merchantFeeRate
            achPaymentEnabled
            achPaymentDisableClientAddAccount
            autoPayDisabled
            notificationsEnabled
            notificationsEnabledOwnersOnly
            internalVideoConfEnabled
            perLocationSms
            smsFeatureEnabled
            mailchimpFeatureEnabled
            smsTier
            maxPerFileSizeUsageMegaBytes
            maxFileSizeUsageMegaBytes
            currentFileSizeUsageMegaBytes
            smsCountTotal
            lessonMateFeatureEnabled
            videoRecordingRequestEnabled
            marketingPlusEnabled
            campaignFeatureEnabled
            ongoingCampaignsEnabled
            advancedPayrollSettingsEnabled
            voipEnabled
            landingPages {
                widgetLinks
                link
            }
            emailDomains {
                domain
                status
                dnsRecords {
                    type
                    name
                    value
                }
            }
            marketingPlusReviewWidgetEnabled
            marketingPlusReviewWidgetMobileDisplay
            marketingPlusReviewWidgetLocations {
                id
                name
            }
            enableClientPosting
            enableStaffSharedFolder
            clientPostingDisclaimer
            paymentTerminals {
                id
                label
            }
            planAmountDue
            plan {
                accountStatus
                freeUntil
                endAt
                planType
                rate
                currency
                discount
                discountReason
                nextPaymentDue
                lastSuccessfulPayment
                paymentMethod
                paymentGatewayCustomerId
                premiumFeatures {
                    label
                    amount
                    startAt
                }
            }
            prospectActiveStudents
            prospectActiveLocations
            prospectCurrentSoftware
            prospectPainPoints
            mailchimpSyncEnabled
            mailchimpSyncId
            mailchimpSyncListId
            mailchimpLastSync
            mailchimpSyncKey
            mailchimpLastSyncError
            clientStatuses
            defaultClientStatus
            prospectLostStatus
            tagCategories {
                name
                tagType
                tags
            }
            googleTagManagerId
            clientDefaultNotificationOptOuts
            clientDefaultSmsNotificationOptOuts
            staffDefaultNotificationOptOuts
            staffDefaultSmsNotificationOptOuts
            smsAppointmentUpdatesIncludeStaffBio
            webhooksEnabled
            federatedLoginEnabled
            crmEnabled
            advancedReportingEnabled
            webhookKey
            webhooks {
                name
                status
                direction
                trigger
                url
                userAgent
                lastFailureDate
                lastFailureDetails
                lastSuccessDate
            }
            selfRegisterFields {
                id
                name
                fieldType
                showField
                requireField
                fieldChoices
                clientCustomAttribute
                autoTagClient
                conditionalOptions {
                    selectedChoice
                    choices
                }
                conditionalFieldId
            }
            crmLeadCaptureFields {
                id
                name
                fieldType
                showField
                requireField
                fieldChoices
                clientCustomAttribute
                autoTagClient
                conditionalOptions {
                    selectedChoice
                    choices
                }
                conditionalFieldId
            }
            presetDiscounts {
                label
                discountType
                discountTarget
                rate
                repeating
            }
            crmWelcomeMessage
            crmThankYouMessage
            crmAutoAssignStaff {
                staff {
                    id
                    name
                }
                priority
            }
            timeOffPolicy
            timeOffPolicyEnabled
            timeOffCategories
            birthdayMessage
            sendBirthdayMessageToActiveMembers
            photoReleaseEnabled
            photoReleaseMigratedAt
            photoReleaseMigrationRequired
            includeStudentsNameInIcal
            paymentFailedTemplate
            paymentReminderTemplate
            dataCollectionPrivacyPolicyUrl
            messagingTermsAndConditionsUrl
            aiCustomizableCallAnalysisPrompt
            variableRatePlanDefaultHouseholdDiscountTarget
            tuitionPolicyEnabled
            mobilePaymentsBeta
            version
        }
    }
`;

/** List load for business editor: scalars, ID lists; services id+name (no plans); light staff/locations for list rows. */
export const BUSINESS_WIDGET_LINKS_QUERY = gql`
    query businessWidgetLinksQuery($businessName: ID!) {
        business(business: $businessName) {
            name
            widgetLinks {
                link
                title
                description
                photo
                widgetType
                serviceIds
                paymentPlanTypes
                staffIds
                locationIds
                visibleByVisitors
                callToAction
                callToActionTitle
                checkWeekly
                defaultPrimarySelection
                services {
                    id
                    name
                }
                staff {
                    id
                    name
                    profilePhoto
                    staffSecondaryAttribute
                }
                locations {
                    id
                    name
                }
            }
        }
    }
`;

/** Full nested widget link for edit modal (plans, staff, locations). */
export const BUSINESS_WIDGET_LINK_DETAIL_QUERY = gql`
    query businessWidgetLinkDetailQuery($businessName: ID!, $link: ID!) {
        business(business: $businessName) {
            name
            widgetLink(link: $link) {
                link
                title
                description
                photo
                widgetType
                serviceIds
                paymentPlanTypes
                staffIds
                locationIds
                visibleByVisitors
                callToAction
                callToActionTitle
                checkWeekly
                defaultPrimarySelection
                services {
                    id
                    durationMinutes
                    hasSessions
                    name
                    deactivated
                    plans {
                        name
                        onlyPayableWithCredit
                        paymentInterval
                        enabledForSelfBooking
                        paymentType
                        visits
                        visitPreEnroll
                        visitPreEnrollHoldDays
                        locations {
                            id
                        }
                    }
                    requiresBooking
                    tags
                }
                staff {
                    id
                    name
                    profilePhoto
                    staffSecondaryAttribute
                }
                locations {
                    id
                    name
                }
            }
        }
    }
`;

export const BUSINESS_WITH_PAYMENT_TERMINALS_QUERY = gql`
    query businessWithTerminalsQuery($businessName: ID!) {
        business(business: $businessName) {
            name
            fullName
            contactEmail
            emailFrom
            uiLogoUrl
            uiBackgroundImageUrl
            uiPrimaryColor
            uiSecondaryColor
            paymentGatewayId
            paymentTerminals {
                id
                label
            }
        }
    }
`;

export const SELF_REGISTER_BUSINESS = gql`
    mutation selfRegisterBusiness(
        $firstName: String!
        $lastName: String!
        $email: Email!
        $phoneNumber: String!
        $businessFullName: String!
        $business: ID!
        $businessWebsite: String!
        $country: String!
        $locale: String!
        $prospectActiveStudents: String
        $prospectActiveLocations: String
        $prospectCurrentSoftware: String
        $prospectPainPoints: String
    ) {
        selfRegisterBusiness(
            firstName: $firstName
            lastName: $lastName
            email: $email
            phoneNumber: $phoneNumber
            business: $business
            businessFullName: $businessFullName
            businessWebsite: $businessWebsite
            country: $country
            locale: $locale
            prospectActiveStudents: $prospectActiveStudents
            prospectActiveLocations: $prospectActiveLocations
            prospectCurrentSoftware: $prospectCurrentSoftware
            prospectPainPoints: $prospectPainPoints
        ) {
            status
            error
        }
    }
`;

export const UPDATE_BUSINESS = gql`
    mutation updateBusiness(
        $business: ID!
        $fullName: String
        $status: BusinessStatus
        $country: String
        $locale: Locale
        $timezone: Timezone
        $defaultCurrency: Currency
        $taxId: String
        $merchantId: String
        $migrateToOpus1Payments: Boolean
        $enableCombinedPayments: Boolean
        $achMerchantId: String
        $stripeCustomersOnPlatform: Boolean
        $collectBillingPostalCode: Boolean
        $merchantFeeRate: Int
        $contactEmail: String
        $businessWebsite: String
        $facebookLink: String
        $twitterLink: String
        $instagramLink: String
        $businessPhoneNumber: String
        $smsPhoneNumber: String
        $uiLogoUrl: String
        $uiIconUrl: String
        $uiFaviconUrl: String
        $uiBackgroundImageUrl: String
        $uiFormBackgroundImageUrl: String
        $uiBackgroundColor: String
        $uiAlternateBackgroundColor: String
        $uiPrimaryColor: String
        $uiSecondaryColor: String
        $uiPrimaryFontColor: String
        $uiSecondaryFontColor: String
        $uiSessionSelfBookingDefaultView: String
        $hideSignupFeeInSelfBooking: Boolean
        $hidePricingOnClientProfile: Boolean
        $hideStartingAtPricingInSelfBooking: Boolean
        $genderDisplayOption: String
        $formsPhoneNumberRequired: Boolean
        $clientCustomAttributes: [String]
        $clientStatuses: [String]
        $defaultClientStatus: String
        $prospectLostStatus: String
        $tagCategories: [TagCategoryInput]
        $welcomeMessage: SanitizedHTML
        $welcomeEmailClient: SanitizedHTML
        $welcomeEmailStaff: SanitizedHTML
        $promotionalMessages: [PromotionalMessageInput]
        $googleReviewEmailSubject: String
        $googleReviewPrompt: SanitizedHTML
        $enableGoogleReviewPrompt: Boolean
        $emailFrom: String
        $bccEmail: String
        $tagline: String
        $footer: String
        $paymentGatewayId: PaymentGateway
        $paymentGatewayApiKey: Secret
        $paymentGatewayApiId: String
        $achPaymentGatewayApiKey: Secret
        $achPaymentGatewayApiId: String
        $achPaymentEnabled: Boolean
        $achPaymentDisableClientAddAccount: Boolean
        $autoPayDisabled: Boolean
        $notificationsEnabled: Boolean!
        $notificationsEnabledOwnersOnly: Boolean!
        $internalVideoConfEnabled: Boolean!
        $enableClientPosting: Boolean
        $enableStaffSharedFolder: Boolean
        $clientPostingDisclaimer: SanitizedHTML
        $mailchimpSyncEnabled: Boolean
        $mailchimpSyncId: String
        $mailchimpSyncKey: Secret
        $mailchimpSyncListId: String
        $widgetLinks: [WidgetLinkInput]
        $landingPages: [LandingPageInput]
        $googleTagManagerId: String
        $plan: BusinessPlanInput
        $perLocationSms: Boolean
        $smsFeatureEnabled: Boolean
        $mailchimpFeatureEnabled: Boolean
        $smsTier: Int
        $maxPerFileSizeUsageMegaBytes: Int
        $maxFileSizeUsageMegaBytes: Int
        $currentFileSizeUsageMegaBytes: Int
        $lessonMateFeatureEnabled: Boolean
        $videoRecordingRequestEnabled: Boolean
        $marketingPlusEnabled: Boolean
        $campaignFeatureEnabled: Boolean
        $ongoingCampaignsEnabled: Boolean
        $advancedPayrollSettingsEnabled: Boolean
        $voipEnabled: Boolean
        $clientDefaultNotificationOptOuts: [ActivityTriggerType]
        $clientDefaultSmsNotificationOptOuts: [ActivityTriggerType]
        $staffDefaultNotificationOptOuts: [ActivityTriggerType]
        $staffDefaultSmsNotificationOptOuts: [ActivityTriggerType]
        $smsAppointmentUpdatesIncludeStaffBio: Boolean
        $webhooks: [WebHookInput]
        $selfRegisterFields: [CustomFormFieldInput]
        $crmEnabled: Boolean
        $advancedReportingEnabled: Boolean
        $crmLeadCaptureFields: [CustomFormFieldInput]
        $crmWelcomeMessage: SanitizedHTML
        $crmThankYouMessage: SanitizedHTML
        $crmAutoAssignStaff: [PrioritizedStaffInput]
        $marketingPlusReviewWidgetEnabled: Boolean
        $marketingPlusReviewWidgetMobileDisplay: Boolean
        $marketingPlusReviewWidgetLocationIds: [ID]
        $webhooksEnabled: Boolean
        $federatedLoginEnabled: Boolean
        $timeOffPolicy: SanitizedHTML
        $timeOffPolicyEnabled: Boolean
        $timeOffCategories: [String]
        $birthdayMessage: SanitizedHTML
        $sendBirthdayMessageToActiveMembers: Boolean
        $photoReleaseEnabled: Boolean
        $includeStudentsNameInIcal: Boolean
        $paymentFailedTemplate: SanitizedHTML
        $paymentReminderTemplate: SanitizedHTML
        $presetDiscounts: [PresetDiscountInput]
        $dataCollectionPrivacyPolicyUrl: String
        $messagingTermsAndConditionsUrl: String
        $aiCustomizableCallAnalysisPrompt: String
        $variableRatePlanDefaultHouseholdDiscountTarget: DiscountTarget
        $tuitionPolicyEnabled: Boolean
        $mobilePaymentsBeta: Boolean
        $stripeTokenizerEnabled: Boolean
        $expectedVersion: Int
    ) {
        updateBusiness(
            business: $business
            fullName: $fullName
            status: $status
            country: $country
            timezone: $timezone
            locale: $locale
            taxId: $taxId
            defaultCurrency: $defaultCurrency
            contactEmail: $contactEmail
            businessWebsite: $businessWebsite
            businessPhoneNumber: $businessPhoneNumber
            smsPhoneNumber: $smsPhoneNumber
            facebookLink: $facebookLink
            twitterLink: $twitterLink
            instagramLink: $instagramLink
            googleTagManagerId: $googleTagManagerId
            uiLogoUrl: $uiLogoUrl
            uiIconUrl: $uiIconUrl
            uiFaviconUrl: $uiFaviconUrl
            uiBackgroundImageUrl: $uiBackgroundImageUrl
            uiFormBackgroundImageUrl: $uiFormBackgroundImageUrl
            uiBackgroundColor: $uiBackgroundColor
            uiAlternateBackgroundColor: $uiAlternateBackgroundColor
            uiPrimaryColor: $uiPrimaryColor
            uiSecondaryColor: $uiSecondaryColor
            uiPrimaryFontColor: $uiPrimaryFontColor
            uiSecondaryFontColor: $uiSecondaryFontColor
            uiSessionSelfBookingDefaultView: $uiSessionSelfBookingDefaultView
            hideSignupFeeInSelfBooking: $hideSignupFeeInSelfBooking
            hidePricingOnClientProfile: $hidePricingOnClientProfile
            hideStartingAtPricingInSelfBooking: $hideStartingAtPricingInSelfBooking
            genderDisplayOption: $genderDisplayOption
            formsPhoneNumberRequired: $formsPhoneNumberRequired
            welcomeMessage: $welcomeMessage
            welcomeEmailClient: $welcomeEmailClient
            welcomeEmailStaff: $welcomeEmailStaff
            promotionalMessages: $promotionalMessages
            googleReviewEmailSubject: $googleReviewEmailSubject
            googleReviewPrompt: $googleReviewPrompt
            enableGoogleReviewPrompt: $enableGoogleReviewPrompt
            clientCustomAttributes: $clientCustomAttributes
            clientStatuses: $clientStatuses
            defaultClientStatus: $defaultClientStatus
            prospectLostStatus: $prospectLostStatus
            tagCategories: $tagCategories
            emailFrom: $emailFrom
            bccEmail: $bccEmail
            tagline: $tagline
            footer: $footer
            merchantId: $merchantId
            migrateToOpus1Payments: $migrateToOpus1Payments
            enableCombinedPayments: $enableCombinedPayments
            achMerchantId: $achMerchantId
            stripeCustomersOnPlatform: $stripeCustomersOnPlatform
            collectBillingPostalCode: $collectBillingPostalCode
            merchantFeeRate: $merchantFeeRate
            paymentGatewayId: $paymentGatewayId
            paymentGatewayApiId: $paymentGatewayApiId
            paymentGatewayApiKey: $paymentGatewayApiKey
            achPaymentGatewayApiId: $achPaymentGatewayApiId
            achPaymentGatewayApiKey: $achPaymentGatewayApiKey
            achPaymentEnabled: $achPaymentEnabled
            achPaymentDisableClientAddAccount: $achPaymentDisableClientAddAccount
            autoPayDisabled: $autoPayDisabled
            notificationsEnabled: $notificationsEnabled
            notificationsEnabledOwnersOnly: $notificationsEnabledOwnersOnly
            internalVideoConfEnabled: $internalVideoConfEnabled
            enableClientPosting: $enableClientPosting
            enableStaffSharedFolder: $enableStaffSharedFolder
            clientPostingDisclaimer: $clientPostingDisclaimer
            mailchimpSyncEnabled: $mailchimpSyncEnabled
            mailchimpSyncId: $mailchimpSyncId
            mailchimpSyncKey: $mailchimpSyncKey
            mailchimpSyncListId: $mailchimpSyncListId
            widgetLinks: $widgetLinks
            landingPages: $landingPages
            plan: $plan
            perLocationSms: $perLocationSms
            smsFeatureEnabled: $smsFeatureEnabled
            mailchimpFeatureEnabled: $mailchimpFeatureEnabled
            smsTier: $smsTier
            maxPerFileSizeUsageMegaBytes: $maxPerFileSizeUsageMegaBytes
            maxFileSizeUsageMegaBytes: $maxFileSizeUsageMegaBytes
            currentFileSizeUsageMegaBytes: $currentFileSizeUsageMegaBytes
            lessonMateFeatureEnabled: $lessonMateFeatureEnabled
            videoRecordingRequestEnabled: $videoRecordingRequestEnabled
            marketingPlusEnabled: $marketingPlusEnabled
            campaignFeatureEnabled: $campaignFeatureEnabled
            ongoingCampaignsEnabled: $ongoingCampaignsEnabled
            advancedPayrollSettingsEnabled: $advancedPayrollSettingsEnabled
            voipEnabled: $voipEnabled
            clientDefaultNotificationOptOuts: $clientDefaultNotificationOptOuts
            clientDefaultSmsNotificationOptOuts: $clientDefaultSmsNotificationOptOuts
            staffDefaultNotificationOptOuts: $staffDefaultNotificationOptOuts
            staffDefaultSmsNotificationOptOuts: $staffDefaultSmsNotificationOptOuts
            smsAppointmentUpdatesIncludeStaffBio: $smsAppointmentUpdatesIncludeStaffBio
            webhooks: $webhooks
            selfRegisterFields: $selfRegisterFields
            crmEnabled: $crmEnabled
            advancedReportingEnabled: $advancedReportingEnabled
            crmLeadCaptureFields: $crmLeadCaptureFields
            crmWelcomeMessage: $crmWelcomeMessage
            crmThankYouMessage: $crmThankYouMessage
            crmAutoAssignStaff: $crmAutoAssignStaff
            marketingPlusReviewWidgetEnabled: $marketingPlusReviewWidgetEnabled
            marketingPlusReviewWidgetMobileDisplay: $marketingPlusReviewWidgetMobileDisplay
            marketingPlusReviewWidgetLocationIds: $marketingPlusReviewWidgetLocationIds
            webhooksEnabled: $webhooksEnabled
            federatedLoginEnabled: $federatedLoginEnabled
            timeOffPolicy: $timeOffPolicy
            timeOffPolicyEnabled: $timeOffPolicyEnabled
            timeOffCategories: $timeOffCategories
            birthdayMessage: $birthdayMessage
            sendBirthdayMessageToActiveMembers: $sendBirthdayMessageToActiveMembers
            photoReleaseEnabled: $photoReleaseEnabled
            includeStudentsNameInIcal: $includeStudentsNameInIcal
            paymentFailedTemplate: $paymentFailedTemplate
            paymentReminderTemplate: $paymentReminderTemplate
            presetDiscounts: $presetDiscounts
            dataCollectionPrivacyPolicyUrl: $dataCollectionPrivacyPolicyUrl
            messagingTermsAndConditionsUrl: $messagingTermsAndConditionsUrl
            aiCustomizableCallAnalysisPrompt: $aiCustomizableCallAnalysisPrompt
            variableRatePlanDefaultHouseholdDiscountTarget: $variableRatePlanDefaultHouseholdDiscountTarget
            tuitionPolicyEnabled: $tuitionPolicyEnabled
            mobilePaymentsBeta: $mobilePaymentsBeta
            stripeTokenizerEnabled: $stripeTokenizerEnabled
            expectedVersion: $expectedVersion
        ) {
            error
            status
            code
            business {
                ...businessAfterUpdate
            }
        }
    }
    ${BusinessAfterUpdateFragment}
`;

export const SELF_UPDATE_BUSINESS = gql`
    mutation selfUpdateBusiness(
        $business: ID!
        $fullName: String
        $country: String
        $locale: Locale
        $timezone: Timezone
        $taxId: String
        $businessWebsite: String
        $businessPhoneNumber: String
        $facebookLink: String
        $twitterLink: String
        $instagramLink: String
        $uiLogoUrl: String
        $uiIconUrl: String
        $uiFaviconUrl: String
        $uiBackgroundImageUrl: String
        $uiFormBackgroundImageUrl: String
        $uiBackgroundColor: String
        $uiAlternateBackgroundColor: String
        $uiPrimaryColor: String
        $uiSecondaryColor: String
        $uiPrimaryFontColor: String
        $uiSecondaryFontColor: String
        $uiSessionSelfBookingDefaultView: String
        $hideSignupFeeInSelfBooking: Boolean
        $hidePricingOnClientProfile: Boolean
        $hideStartingAtPricingInSelfBooking: Boolean
        $genderDisplayOption: String
        $clientCustomAttributes: [String]
        $clientStatuses: [String]
        $defaultClientStatus: String
        $prospectLostStatus: String
        $tagCategories: [TagCategoryInput]
        $welcomeMessage: SanitizedHTML
        $welcomeEmailClient: SanitizedHTML
        $welcomeEmailStaff: SanitizedHTML
        $promotionalMessages: [PromotionalMessageInput]
        $googleReviewEmailSubject: String
        $googleReviewPrompt: SanitizedHTML
        $enableGoogleReviewPrompt: Boolean
        $emailFrom: String
        $bccEmail: String
        $googleTagManagerId: String
        $tagline: String
        $footer: String
        $enableClientPosting: Boolean
        $enableStaffSharedFolder: Boolean
        $clientPostingDisclaimer: SanitizedHTML
        $mailchimpSyncEnabled: Boolean
        $mailchimpSyncId: String
        $mailchimpSyncKey: Secret
        $mailchimpSyncListId: String
        $widgetLinks: [WidgetLinkInput]
        $landingPages: [LandingPageInput]
        $clientDefaultNotificationOptOuts: [ActivityTriggerType]
        $clientDefaultSmsNotificationOptOuts: [ActivityTriggerType]
        $staffDefaultNotificationOptOuts: [ActivityTriggerType]
        $staffDefaultSmsNotificationOptOuts: [ActivityTriggerType]
        $smsAppointmentUpdatesIncludeStaffBio: Boolean
        $webhooks: [WebHookInput]
        $selfRegisterFields: [CustomFormFieldInput]
        $crmLeadCaptureFields: [CustomFormFieldInput]
        $crmWelcomeMessage: SanitizedHTML
        $crmThankYouMessage: SanitizedHTML
        $crmAutoAssignStaff: [PrioritizedStaffInput]
        $marketingPlusReviewWidgetEnabled: Boolean
        $marketingPlusReviewWidgetMobileDisplay: Boolean
        $marketingPlusReviewWidgetLocationIds: [ID]
        $timeOffPolicy: SanitizedHTML
        $timeOffPolicyEnabled: Boolean
        $timeOffCategories: [String]
        $birthdayMessage: SanitizedHTML
        $sendBirthdayMessageToActiveMembers: Boolean
        $includeStudentsNameInIcal: Boolean
        $paymentFailedTemplate: SanitizedHTML
        $paymentReminderTemplate: SanitizedHTML
        $presetDiscounts: [PresetDiscountInput]
        $dataCollectionPrivacyPolicyUrl: String
        $messagingTermsAndConditionsUrl: String
        $expectedVersion: Int
    ) {
        selfUpdateBusiness(
            business: $business
            fullName: $fullName
            country: $country
            timezone: $timezone
            locale: $locale
            taxId: $taxId
            businessWebsite: $businessWebsite
            businessPhoneNumber: $businessPhoneNumber
            facebookLink: $facebookLink
            twitterLink: $twitterLink
            instagramLink: $instagramLink
            uiLogoUrl: $uiLogoUrl
            uiIconUrl: $uiIconUrl
            uiFaviconUrl: $uiFaviconUrl
            uiBackgroundImageUrl: $uiBackgroundImageUrl
            uiFormBackgroundImageUrl: $uiFormBackgroundImageUrl
            uiBackgroundColor: $uiBackgroundColor
            uiAlternateBackgroundColor: $uiAlternateBackgroundColor
            uiPrimaryColor: $uiPrimaryColor
            uiSecondaryColor: $uiSecondaryColor
            uiPrimaryFontColor: $uiPrimaryFontColor
            uiSecondaryFontColor: $uiSecondaryFontColor
            uiSessionSelfBookingDefaultView: $uiSessionSelfBookingDefaultView
            hideSignupFeeInSelfBooking: $hideSignupFeeInSelfBooking
            hidePricingOnClientProfile: $hidePricingOnClientProfile
            hideStartingAtPricingInSelfBooking: $hideStartingAtPricingInSelfBooking
            genderDisplayOption: $genderDisplayOption
            clientCustomAttributes: $clientCustomAttributes
            clientStatuses: $clientStatuses
            defaultClientStatus: $defaultClientStatus
            prospectLostStatus: $prospectLostStatus
            tagCategories: $tagCategories
            welcomeMessage: $welcomeMessage
            welcomeEmailClient: $welcomeEmailClient
            welcomeEmailStaff: $welcomeEmailStaff
            promotionalMessages: $promotionalMessages
            googleReviewEmailSubject: $googleReviewEmailSubject
            googleReviewPrompt: $googleReviewPrompt
            enableGoogleReviewPrompt: $enableGoogleReviewPrompt
            emailFrom: $emailFrom
            bccEmail: $bccEmail
            googleTagManagerId: $googleTagManagerId
            tagline: $tagline
            footer: $footer
            enableClientPosting: $enableClientPosting
            enableStaffSharedFolder: $enableStaffSharedFolder
            clientPostingDisclaimer: $clientPostingDisclaimer
            mailchimpSyncEnabled: $mailchimpSyncEnabled
            mailchimpSyncId: $mailchimpSyncId
            mailchimpSyncKey: $mailchimpSyncKey
            mailchimpSyncListId: $mailchimpSyncListId
            widgetLinks: $widgetLinks
            landingPages: $landingPages
            clientDefaultNotificationOptOuts: $clientDefaultNotificationOptOuts
            clientDefaultSmsNotificationOptOuts: $clientDefaultSmsNotificationOptOuts
            staffDefaultNotificationOptOuts: $staffDefaultNotificationOptOuts
            staffDefaultSmsNotificationOptOuts: $staffDefaultSmsNotificationOptOuts
            smsAppointmentUpdatesIncludeStaffBio: $smsAppointmentUpdatesIncludeStaffBio
            webhooks: $webhooks
            selfRegisterFields: $selfRegisterFields
            crmLeadCaptureFields: $crmLeadCaptureFields
            crmWelcomeMessage: $crmWelcomeMessage
            crmThankYouMessage: $crmThankYouMessage
            crmAutoAssignStaff: $crmAutoAssignStaff
            marketingPlusReviewWidgetEnabled: $marketingPlusReviewWidgetEnabled
            marketingPlusReviewWidgetMobileDisplay: $marketingPlusReviewWidgetMobileDisplay
            marketingPlusReviewWidgetLocationIds: $marketingPlusReviewWidgetLocationIds
            timeOffPolicy: $timeOffPolicy
            timeOffPolicyEnabled: $timeOffPolicyEnabled
            timeOffCategories: $timeOffCategories
            birthdayMessage: $birthdayMessage
            sendBirthdayMessageToActiveMembers: $sendBirthdayMessageToActiveMembers
            includeStudentsNameInIcal: $includeStudentsNameInIcal
            paymentFailedTemplate: $paymentFailedTemplate
            paymentReminderTemplate: $paymentReminderTemplate
            presetDiscounts: $presetDiscounts
            dataCollectionPrivacyPolicyUrl: $dataCollectionPrivacyPolicyUrl
            messagingTermsAndConditionsUrl: $messagingTermsAndConditionsUrl
            expectedVersion: $expectedVersion
        ) {
            error
            status
            code
            business {
                ...businessAfterUpdate
            }
        }
    }
    ${BusinessAfterUpdateFragment}
`;

export const BUSINESS_PROGRESS_QUERY = gql`
    query businessProgressQuery($businessName: ID!) {
        businessProgress(business: $businessName) {
            showPaymentWarning
            showActivePaymentWarning
        }
    }
`;

export const ADD_BUSINESS_CUSTOM_LINK = gql`
    mutation addBusinessLink($business: ID!, $widgetLink: WidgetLinkInput!) {
        addBusinessLink(business: $business, widgetLink: $widgetLink) {
            error
            status
            business {
                ...detailedBusiness
            }
        }
    }
    ${DetailedBusinessFragment}
`;

export const CUSTOMER_JOURNEY_PHASE_FIELDS = gql`
    fragment CustomerJourneyPhaseFields on CustomerJourneyPhase {
        business
        phaseType
        dataRequirements {
            id
            fieldName
            fieldType
            fieldRequirement
            isConfigurable
            isBuiltin
            order
            fieldOptions
            conditionalOptions {
                selectedChoice
                options
            }
            conditionalFieldId
            clientCustomAttribute
            autoTagClient
            isAccountManagerInformation
        }
        createdAt
        version
        additionalConfig {
            bookingLink {
                type
                link
            }
            thankYouMessage
            welcomeMessage
        }
    }
`;

export const CUSTOMER_JOURNEY_PHASE_QUERY = gql`
    query GetCustomerJourneyPhases($business: String!) {
        customerJourneyPhases(business: $business) {
            ...CustomerJourneyPhaseFields
        }
    }
    ${CUSTOMER_JOURNEY_PHASE_FIELDS}
`;

export const CUSTOMER_JOURNEY_PHASE_BY_TYPE_QUERY = gql`
    query GetCustomerJourneyPhaseByType(
        $business: String!
        $phaseType: CustomerJourneyPhaseType!
    ) {
        customerJourneyPhaseByType(business: $business, phaseType: $phaseType) {
            business
            phaseType
            dataRequirements {
                id
                fieldName
                fieldType
                fieldRequirement
                isConfigurable
                isBuiltin
                order
                fieldOptions
                conditionalOptions {
                    selectedChoice
                    options
                }
                conditionalFieldId
                clientCustomAttribute
                autoTagClient
                isAccountManagerInformation
            }
            createdAt
            additionalConfig {
                bookingLink {
                    type
                    link
                }
                thankYouMessage
                welcomeMessage
            }
        }
    }
`;

export const UPDATE_CUSTOMER_JOURNEY_PHASES = gql`
    mutation updateCustomerJourneyPhases(
        $business: String!
        $phases: [PhaseUpdateInput!]!
    ) {
        updateCustomerJourneyPhases(business: $business, phases: $phases) {
            status
            error
            phases {
                ...CustomerJourneyPhaseFields
            }
        }
    }
    ${CUSTOMER_JOURNEY_PHASE_FIELDS}
`;

const MULTI_ENTITY_SETUP_BLOCKER_FIELDS = gql`
    fragment MultiEntitySetupBlockerFields on MultiEntitySetupStatus {
        state
        message
        missingClientCopyCount
        unmatchedCustomerCount
        canOverride
        blockingCustomers {
            reason
            stripeCustomerId
            email
            merchantId
            personId
            name
        }
    }
`;

export const RECHECK_MULTI_ENTITY_SETUP = gql`
    mutation recheckMultiEntitySetup($business: String!) {
        recheckMultiEntitySetup(business: $business) {
            status
            error
            business {
                name
                stripeCustomersOnPlatform
            }
            multiEntitySetupStatus {
                ...MultiEntitySetupBlockerFields
                vaultedCardCount
                distinctActiveMerchantCount
            }
        }
    }
    ${MULTI_ENTITY_SETUP_BLOCKER_FIELDS}
`;

export const OVERRIDE_MULTI_ENTITY_SETUP = gql`
    mutation overrideMultiEntitySetup($business: String!) {
        overrideMultiEntitySetup(business: $business) {
            status
            error
            business {
                name
                stripeCustomersOnPlatform
            }
            multiEntitySetupStatus {
                ...MultiEntitySetupBlockerFields
            }
        }
    }
    ${MULTI_ENTITY_SETUP_BLOCKER_FIELDS}
`;

export const RETURN_TO_SINGLE_ENTITY = gql`
    mutation returnToSingleEntity($business: String!) {
        returnToSingleEntity(business: $business) {
            status
            error
            business {
                name
                stripeCustomersOnPlatform
            }
            multiEntitySetupStatus {
                state
                message
            }
        }
    }
`;

export default BUSINESS_QUERY;
